import React, { useEffect, useMemo, useState } from "react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	Legend,
	ResponsiveContainer,
	CartesianGrid,
} from "recharts";
import {
	CheckCircle2,
	Loader2,
	ListTodo,
	PauseCircle,
	PlayCircle,
} from "lucide-react";
import CreateTicketModal from "../Modal/CreateTicketModal";
import CreateTaskModal from "../Task/Modal/CreateTaskModal";
import FrameBG from "../../Assets/FrameBGDashboard.png";
import { getUserYearlyTaskReport } from "../../Services/ReportService";

const SURFACE_COLORS = {
	primary: "#003a63",
	secondary: "#13659C",
	soft: "#BCD4E8",
	pale: "#D1E3FF",
	accent: "#FFD166",
	accentSoft: "#FFF1BF",
	border: "#D9E7F2",
	tealDark: "#219EBC",
};

const Home = () => {
	const currentYear = new Date().getFullYear();
	const [showModal, setShowModal] = useState(false);
	const [showTaskModal, setShowTaskModal] = useState(false);
	const [selectedYear, setSelectedYear] = useState(String(currentYear));
	const [reportRows, setReportRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		const fetchYearlyReport = async () => {
			try {
				setLoading(true);
				setError("");

				const res = await getUserYearlyTaskReport({ year: selectedYear });
				setReportRows(res?.data?.taskReport || []);
			} catch (err) {
				console.error("Failed to fetch user yearly task report", err);
				setReportRows([]);
				setError("Unable to load yearly task report.");
			} finally {
				setLoading(false);
			}
		};

		fetchYearlyReport();
	}, [selectedYear]);

	const yearOptions = Array.from({ length: 6 }, (_, index) =>
		String(currentYear - index),
	);

	const { monthlyRows, totalsRow, chartData, statusData, userName } = useMemo(() => {
		const rows = Array.isArray(reportRows) ? reportRows : [];
		const totals = rows.find((row) => row.monthNo === 13) || null;
		const months = rows
			.filter((row) => row.monthNo !== 13)
			.sort((a, b) => a.monthNo - b.monthNo);

		return {
			monthlyRows: months,
			totalsRow: totals,
			chartData: months.map((row) => ({
				month: row.monthName,
				Assigned: row.createdTasks,
				Closed: row.closedTasks,
				"Under Review": row.completedTasks,
				Open: row.openTasks,
				"In Progress": row.inProgressTasks,
				Hold: row.holdTasks,
			})),
			statusData: totals
				? [
						{ name: "Assigned", value: totals.createdTasks, color: SURFACE_COLORS.primary },
						{ name: "Closed", value: totals.closedTasks, color: SURFACE_COLORS.accent },
						{ name: "Under Review", value: totals.completedTasks, color: "#34d399" },
						{ name: "Open", value: totals.openTasks, color: SURFACE_COLORS.pale },
						{ name: "In Progress", value: totals.inProgressTasks, color: SURFACE_COLORS.tealDark },
						{ name: "Hold", value: totals.holdTasks, color: SURFACE_COLORS.soft },
				  ]
				: [],
			userName: rows[0]?.userName || "User",
		};
	}, [reportRows]);

	const summaryCards = [
		{
			label: "Assigned Tasks",
			value: totalsRow?.createdTasks ?? 0,
			icon: ListTodo,
		},
		{
			label: "Closed Tasks",
			value: totalsRow?.closedTasks ?? 0,
			icon: CheckCircle2,
		},
		{
			label: "Under Review Tasks",
			value: totalsRow?.completedTasks ?? 0,
			icon: CheckCircle2,
		},
		{
			label: "In Progress",
			value: totalsRow?.inProgressTasks ?? 0,
			icon: PlayCircle,
		},
		{
			label: "Hold Tasks",
			value: totalsRow?.holdTasks ?? 0,
			icon: PauseCircle,
		},
	];

	return (
		<>
			<div className="relative z-10 space-y-6">
				<div
					className="relative rounded-lg overflow-hidden shadow-md text-white mt-4"
					style={{
						backgroundImage: `url(${FrameBG})`,
						backgroundSize: "cover",
						backgroundPosition: "center",
					}}
				>
					<div className="p-8 flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
						<div>
							<h2 className="text-xl font-semibold">Your yearly task dashboard</h2>
							<p className="text-base opacity-90">
								Track task activity for {userName} in {selectedYear}
							</p>
						</div>
						<div className="flex flex-wrap items-center gap-3">
							<select
								value={selectedYear}
								onChange={(e) => setSelectedYear(e.target.value)}
								className="rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none border"
								style={{
									boxShadow: "none",
									borderColor: SURFACE_COLORS.border,
								}}
							>
								{yearOptions.map((year) => (
									<option key={year} value={year}>
										{year}
									</option>
								))}
							</select>
							<button
								onClick={() => setShowTaskModal(true)}
								className="bg-[#003a63] text-white px-5 py-2 rounded-lg me-3"
							>
								Add Task
							</button>

							<button
								onClick={() => setShowModal(true)}
								className="bg-[#003a63] text-white px-5 py-2 rounded-lg"
							>
								Add Ticket
							</button>
						</div>
					</div>
				</div>

				<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
					<div>
						<h3 className="text-xl font-semibold text-gray-700">Insights</h3>
						<p className="text-sm text-gray-500">
							Month-wise task summary from yearly report data
						</p>
					</div>
					<div className="text-sm text-gray-500">
						{loading ? "Refreshing report data..." : `${selectedYear} report`}
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
					{summaryCards.map((card) => {
						const Icon = card.icon;

						return (
							<div
								key={card.label}
								className="bg-white p-4 rounded-lg shadow flex justify-between items-center border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<div>
									<p className="text-gray-500">{card.label}</p>
									<h2
										className="text-2xl font-bold mt-1"
										style={{ color: SURFACE_COLORS.primary }}
									>
										{card.value}
									</h2>
								</div>
								<div
									className="p-2 rounded-full"
									style={{
										backgroundColor: SURFACE_COLORS.pale,
										color: SURFACE_COLORS.primary,
									}}
								>
									<Icon className="w-8 h-8" />
								</div>
							</div>
						);
					})}
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<div
						className="bg-white/90 backdrop-blur-[1px] p-4 rounded-lg shadow border"
						style={{ borderColor: SURFACE_COLORS.border }}
					>
						<div className="flex justify-between items-center mb-3">
							<div>
								<h3 className="font-semibold">Monthly task trend</h3>
								<p className="text-sm text-gray-500">
									Assigned, closed, under review and active task flow
								</p>
							</div>
						</div>
						<div className="h-[280px]">
							{loading ? (
								<div className="h-full flex items-center justify-center text-gray-500">
									<Loader2 className="animate-spin mr-2" size={18} />
									Loading monthly report...
								</div>
							) : chartData.length > 0 ? (
								<ResponsiveContainer width="100%" height="100%">
									<LineChart data={chartData}>
										<CartesianGrid
											strokeDasharray="3 3"
											vertical={false}
											stroke={SURFACE_COLORS.border}
										/>
										<XAxis dataKey="month" />
										<YAxis allowDecimals={false} />
										<Tooltip />
										<Legend />
										<Line
											type="monotone"
											dataKey="Assigned"
											stroke={SURFACE_COLORS.primary}
											strokeWidth={2.5}
										/>
										<Line
											type="monotone"
											dataKey="Closed"
											stroke={SURFACE_COLORS.accent}
											strokeWidth={2.5}
										/>
										<Line
											type="monotone"
											dataKey="Under Review"
											stroke="#34d399"
											strokeWidth={2.5}
										/>
										<Line
											type="monotone"
											dataKey="Open"
											stroke="#7aa6d8"
											strokeWidth={2.5}
										/>
										<Line
											type="monotone"
											dataKey="In Progress"
											stroke={SURFACE_COLORS.tealDark}
											strokeWidth={2.5}
										/>
										<Line
											type="monotone"
											dataKey="Hold"
											stroke={SURFACE_COLORS.soft}
											strokeWidth={2.5}
										/>
									</LineChart>
								</ResponsiveContainer>
							) : (
								<div className="h-full flex items-center justify-center text-gray-400">
									No monthly task data available
								</div>
							)}
						</div>
					</div>

					<div
						className="bg-white p-6 rounded-lg shadow-md flex flex-col border"
						style={{ borderColor: SURFACE_COLORS.border }}
					>
						<div className="flex justify-between items-center mb-4">
							<div>
								<h3 className="font-semibold text-gray-700">
									Yearly status summary
								</h3>
								<p className="text-sm text-gray-500">
									Readable comparison of yearly task counts
								</p>
							</div>
							<span className="text-sm text-gray-500">
								{totalsRow?.createdTasks ?? 0} assigned
							</span>
						</div>

						<div className="grid grid-cols-2 gap-3">
							{loading ? (
								<div className="col-span-2 h-[280px] flex items-center justify-center text-gray-500">
									<Loader2 className="animate-spin mr-2" size={18} />
									Loading yearly summary...
								</div>
							) : statusData.some((item) => item.value > 0) ? (
								statusData.map((item) => (
									<div
										key={item.name}
										className="rounded-lg p-4 min-h-[118px] flex flex-col justify-between"
										style={{ backgroundColor: `${item.color}20` }}
									>
										<p className="text-xs text-gray-500">{item.name}</p>
										<p
											className="text-2xl font-bold mt-2"
											style={{ color: SURFACE_COLORS.primary }}
										>
											{item.value}
										</p>
									</div>
								))
							) : (
								<div className="col-span-2 h-[280px] flex items-center justify-center text-gray-400">
									No yearly summary available
								</div>
							)}
						</div>
					</div>
				</div>

				<div
					className="bg-white p-4 rounded-lg shadow border"
					style={{ borderColor: SURFACE_COLORS.border }}
				>
					<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-3">
						<div>
							<h3 className="font-semibold">Monthly breakdown</h3>
							<p className="text-sm text-gray-500">
								Detailed month-wise yearly task report
							</p>
						</div>
						<span className="text-sm text-gray-500">
							{monthlyRows.length} months with report data
						</span>
					</div>
					<div className="overflow-x-auto">
						<table className="min-w-full text-sm">
							<thead>
								<tr
									className="border-b text-left text-gray-500"
									style={{ borderColor: SURFACE_COLORS.border }}
								>
									<th className="py-3 pr-4 font-medium">Month</th>
									<th className="py-3 px-4 font-medium">Assigned</th>
									<th className="py-3 px-4 font-medium">Closed</th>
									<th className="py-3 px-4 font-medium">Under Review</th>
									<th className="py-3 px-4 font-medium">Hold</th>
									<th className="py-3 px-4 font-medium">Open</th>
									<th className="py-3 px-4 font-medium">In Progress</th>
								</tr>
							</thead>
							<tbody>
								{monthlyRows.map((row) => (
									<tr
										key={row.monthNo}
										className="border-b last:border-b-0"
										style={{ borderColor: SURFACE_COLORS.border }}
									>
										<td className="py-3 pr-4 font-medium text-gray-700">
											{row.monthName}
										</td>
										<td className="py-3 px-4">{row.createdTasks}</td>
										<td className="py-3 px-4">{row.closedTasks}</td>
										<td className="py-3 px-4">{row.completedTasks}</td>
										<td className="py-3 px-4">{row.holdTasks}</td>
										<td className="py-3 px-4">{row.openTasks}</td>
										<td className="py-3 px-4">{row.inProgressTasks}</td>
									</tr>
								))}
								{totalsRow && (
									<tr
										className="font-semibold"
										style={{ backgroundColor: SURFACE_COLORS.pale }}
									>
										<td className="py-3 pr-4">{totalsRow.monthName}</td>
										<td className="py-3 px-4">{totalsRow.createdTasks}</td>
										<td className="py-3 px-4">{totalsRow.closedTasks}</td>
										<td className="py-3 px-4">{totalsRow.completedTasks}</td>
										<td className="py-3 px-4">{totalsRow.holdTasks}</td>
										<td className="py-3 px-4">{totalsRow.openTasks}</td>
										<td className="py-3 px-4">{totalsRow.inProgressTasks}</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>

				{error && (
					<div
						className="text-sm font-medium"
						style={{ color: SURFACE_COLORS.secondary }}
					>
						{error}
					</div>
				)}
			</div>

			<CreateTicketModal
				show={showModal}
				onClose={() => setShowModal(false)}
				onCreated={() => {}}
			/>

			<CreateTaskModal
				show={showTaskModal}
				onClose={() => setShowTaskModal(false)}
				onCreated={() => {}}
			/>
		</>
	);
};

export default Home;
